// Las palabras de Administración · {Tiendas y zonas}, por receta y al
// estilo de adminWords/threadWords: sustantivos desde `recipe.labels`
// («Tienda», «Zona», «Jefe de tienda»), marca desde `recipe.brands`, y los
// roles que pueden ser responsables desde `recipe.memberRoles` por su
// scopeType —el de la zona (`asset_group`) y el de la tienda (`asset`)—,
// nunca por slug ni por clave de rol fija.
//
// El copy del handoff (hints, pies de drawer, toasts) se compone acá para
// que la pantalla y los drawers lean la misma frase.

import type { RecipeMeResponse } from "@bitacorapro/api-types";

export type ActivosRecipe = Pick<RecipeMeResponse, "labels" | "memberRoles"> &
  Partial<Pick<RecipeMeResponse, "brands" | "group" | "rootEntity">>;

export type ActivosWords = {
  /** «Tienda» / «tienda». */
  assetOne: string;
  assetOneLower: string;
  /** «Tiendas» / «tiendas». */
  assetMany: string;
  assetManyLower: string;
  /** «Zona» / «zona». */
  groupOne: string;
  groupOneLower: string;
  /** «Zonas» / «zonas». */
  groupMany: string;
  groupManyLower: string;
  /** Acción primaria de la tab de tiendas: `labels.assetNew` («Nueva tienda»). */
  assetNew: string;
  /** Acción primaria de la tab de zonas («Nueva zona»). */
  groupNew: string;
  /** Columna y campo del responsable de la tienda («Jefe de tienda»). */
  assetLead: string;
  /**
   * La clave de `asset.attributes` que guarda ese nombre
   * (`labels.assetInfoFieldKey`, «jefe_de_tienda»): es lo que escribe la
   * importación de planilla y lo que /cartera (PWA) muestra, así que la
   * columna y el campo del drawer leen y escriben la misma clave.
   */
  assetLeadKey: string | undefined;
  /** Columna del responsable de la zona («Supervisor»). */
  groupLead: string;
  /** GET /asset-groups trae al responsable en `subline` (sublineSource = supervisor). */
  groupLeadFromSubline: boolean;
  /** «Marca», o null cuando la receta no tiene marcas. */
  brand: string | null;
  /** El `kind` que la receta declara para sus grupos («zona»), si lo hay. */
  groupKind: string | undefined;
  /** La receta exige zona en toda tienda (rootEntity = group). */
  groupRequired: boolean;
  /** Claves de rol cuyo alcance es la zona: candidatos a responsable. */
  groupLeadRoleKeys: readonly string[];
  /** Claves de rol cuyo alcance es la tienda. */
  assetLeadRoleKeys: readonly string[];
};

function lower(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

export function activosWords(recipe: ActivosRecipe | undefined): ActivosWords {
  const labels = recipe?.labels;
  const assetOne = labels?.asset ?? "Activo";
  const assetMany = labels?.assetPlural ?? "Activos";
  const groupOne = labels?.assetGroup ?? "Grupo";
  const groupMany = labels?.assetGroupPlural ?? "Grupos";
  const roles = recipe?.memberRoles ?? [];
  return {
    assetOne,
    assetOneLower: lower(assetOne),
    assetMany,
    assetManyLower: lower(assetMany),
    groupOne,
    groupOneLower: lower(groupOne),
    groupMany,
    groupManyLower: lower(groupMany),
    assetNew: labels?.assetNew ?? `Nueva ${lower(assetOne)}`,
    // La receta no declara un «assetGroupNew»; el handoff fija «Nueva
    // zona» para facility y esta sección sólo se monta ahí (plan §2.4).
    groupNew: `Nueva ${lower(groupOne)}`,
    assetLead: labels?.assetInfoField ?? "Responsable",
    assetLeadKey: labels?.assetInfoFieldKey,
    groupLead: labels?.supervisorNoun ?? "Responsable",
    groupLeadFromSubline: recipe?.group?.sublineSource === "supervisor",
    brand: recipe?.brands?.enabled ? recipe.brands.label : null,
    groupKind: recipe?.group?.kind,
    groupRequired: recipe?.rootEntity === "group",
    groupLeadRoleKeys: roles.filter((r) => r.scopeType === "asset_group").map((r) => r.key),
    assetLeadRoleKeys: roles.filter((r) => r.scopeType === "asset").map((r) => r.key),
  };
}

/**
 * El nombre del jefe de tienda tal como lo guarda `asset.attributes` bajo
 * `assetLeadKey`: string no vacío o null. Misma lectura que `infoFieldOf`
 * en /cartera (PWA) y `readOwnerLabel` en core-api, para que las dos
 * pantallas digan lo mismo de la misma tienda.
 */
export function assetLeadFromAttributes(
  attributes: Record<string, unknown> | null | undefined,
  key: string | undefined
): string | null {
  if (!key || !attributes) return null;
  const v = attributes[key];
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/**
 * El nombre que GET /asset-groups trae en `subline` cuando la receta lo
 * deriva del responsable. El servidor compone el texto con el literal
 * «Supervisor: » (routes/asset-groups.ts, sublineFor) sin mirar
 * `labels.supervisorNoun`, así que el prefijo se quita por su forma
 * («<algo>: ») y no por el sustantivo de la receta. Devuelve null cuando
 * no hay subline (zona sin responsable).
 *
 * Es el respaldo para un servidor que aún no manda `supervisorUserId` en
 * la lista: la pantalla cruza este nombre con el roster para
 * preseleccionarlo. Cuando el servidor no puede resolver el nombre
 * devuelve su propio comodín («Supervisor: Supervisor»), que acá sale como
 * «Supervisor» y la pantalla trata como «asignado, sin nombre» porque no
 * coincide con nadie del roster. Con el id en el wire, nada de esto hace
 * falta.
 */
export function groupLeadNameFromSubline(subline: string | undefined): string | null {
  if (!subline) return null;
  const name = subline.replace(/^[^:]+:\s*/, "");
  return name.trim() || null;
}
